import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  Beaker, 
  Calculator, 
  BookOpen,
  Check,
  X,
  Search,
  Filter,
  Save,
  AlertCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from 'sonner';
import apiService from '@/services/api';
import { getCurrentUser } from '@/data/users';

// Stream definitions
const STREAM_SUBJECTS = {
  Science: {
    core: ["English", "Mathematics", "Chemistry", "Biology", "Physics"],
    optional: ["ICT", "Agriculture", "French", "Geography"],
    maxOptional: 1,
    icon: Beaker,
    color: "bg-purple-100 text-purple-800 border-purple-300",
    bgColor: "bg-purple-50"
  },
  Commerce: {
    core: ["English", "Mathematics", "Economics", "Accounting", "ICT"],
    optional: ["French", "Agriculture"],
    maxOptional: 1,
    icon: Calculator,
    color: "bg-green-100 text-green-800 border-green-300",
    bgColor: "bg-green-50"
  },
  Arts: {
    core: ["English", "Mathematics", "History", "Geography", "Development Studies"],
    optional: ["ICT", "Agriculture", "French"],
    maxOptional: 1,
    icon: BookOpen,
    color: "bg-amber-100 text-amber-800 border-amber-300",
    bgColor: "bg-amber-50"
  }
};

// Helper component for stream icons - defined outside main component
const StreamIconComponent = ({ stream }) => {
  const Icon = STREAM_SUBJECTS[stream]?.icon || Users;
  return <Icon className="w-4 h-4" />;
};

export default function StudentSubjectAssignment() {
  const [year, setYear] = useState('11');
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStream, setFilterStream] = useState('all');
  const [loading, setLoading] = useState(false);
  const [bulkStream, setBulkStream] = useState('');
  const [bulkOptional, setBulkOptional] = useState('');
  
  const currentUser = getCurrentUser();

  // Load students for the selected year
  useEffect(() => {
    const loadStudents = async () => {
      setLoading(true);
      try {
        // Get all classes for this year
        const classes = await apiService.listClasses(null, parseInt(year));
        
        // Get unique students from all classes
        const studentMap = new Map();
        
        for (const cls of classes) {
          try {
            const classStudents = await apiService.getClassStudents(cls.id);
            if (classStudents && classStudents.students) {
              classStudents.students.forEach(s => {
                if (!studentMap.has(s.student_id)) {
                  studentMap.set(s.student_id, {
                    id: s.student_id,
                    name: s.name || s.student_id,
                    stream: 'Not Assigned',
                    optional: '',
                    subjects: []
                  });
                }
              });
            }
          } catch (err) {
            console.warn(`Could not load students for class ${cls.id}`);
          }
        }
        
        // Get subject assignments for each student
        const studentsWithSubjects = [];
        for (const [studentId, student] of studentMap) {
          try {
            const subjectData = await apiService.getStudentReportCardSubjects(studentId, parseInt(year));
            student.stream = subjectData.stream || 'Not Assigned';
            student.subjects = subjectData.subjects || [];
            
            // Detect optional subject
            const streamDef = STREAM_SUBJECTS[student.stream];
            if (streamDef) {
              student.optional = student.subjects.find(s => streamDef.optional.includes(s)) || '';
            }
          } catch (err) {
            // Keep default values
          }
          studentsWithSubjects.push(student);
        }
        
        setStudents(studentsWithSubjects);
      } catch (error) {
        console.error('Error loading students:', error);
        toast.error('Failed to load students');
      } finally {
        setLoading(false);
      }
    };

    loadStudents();
  }, [year]);

  // Filter students based on search and stream filter
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           student.id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStream === 'all' || student.stream === filterStream;
      return matchesSearch && matchesFilter;
    });
  }, [students, searchTerm, filterStream]);

  // Handle individual student stream assignment
  const handleStudentStreamChange = async (studentId, stream, optional) => {
    if (!stream || !optional) {
      toast.error('Please select both stream and optional subject');
      return;
    }
    
    const streamDef = STREAM_SUBJECTS[stream];
    if (!streamDef) return;
    
    const subjects = [...streamDef.core, optional];
    
    try {
      await apiService.assignStudentSubjects(studentId, subjects, stream, parseInt(year));
      
      // Update local state
      setStudents(prev => prev.map(s => 
        s.id === studentId 
          ? { ...s, stream, optional, subjects }
          : s
      ));
      
      toast.success(`Assigned ${stream} stream to student`);
    } catch (error) {
      console.error('Error assigning stream:', error);
      toast.error('Failed to assign stream');
    }
  };

  // Handle bulk assignment
  const handleBulkAssign = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    if (!bulkStream || !bulkOptional) {
      toast.error('Please select both stream and optional subject');
      return;
    }
    
    try {
      await apiService.bulkAssignStream(
        parseInt(year),
        bulkStream,
        selectedStudents,
        bulkOptional
      );
      
      // Update local state
      const streamDef = STREAM_SUBJECTS[bulkStream];
      const subjects = [...streamDef.core, bulkOptional];
      
      setStudents(prev => prev.map(s => 
        selectedStudents.includes(s.id)
          ? { ...s, stream: bulkStream, optional: bulkOptional, subjects }
          : s
      ));
      
      setSelectedStudents([]);
      setBulkStream('');
      setBulkOptional('');
      
      toast.success(`Assigned ${bulkStream} stream to ${selectedStudents.length} students`);
    } catch (error) {
      console.error('Error bulk assigning:', error);
      toast.error('Failed to assign streams');
    }
  };

  // Toggle student selection
  const toggleStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Select all filtered students
  const selectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="student-subject-assignment-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Student Subject Assignment</h1>
          <p className="text-gray-500">Assign streams and subjects to Year 11-13 students for report cards</p>
        </div>
      </div>

      {/* Stream Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(STREAM_SUBJECTS).map(([stream, info]) => {
          const Icon = info.icon;
          const count = students.filter(s => s.stream === stream).length;
          return (
            <Card key={stream} className={`${info.bgColor} border-2`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Icon className="w-5 h-5" />
                  {stream} Stream
                  <Badge variant="secondary">{count} students</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p className="font-medium">Core: {info.core.join(', ')}</p>
                <p className="text-gray-600 mt-1">Options: {info.optional.join(', ')} (pick {info.maxOptional})</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Manage Assignments</CardTitle>
          <CardDescription>
            Select students and assign them to streams with their optional subjects
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters Row */}
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm font-medium mb-1 block">Year Level</label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-32" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="11">Year 11</SelectItem>
                  <SelectItem value="12">Year 12</SelectItem>
                  <SelectItem value="13">Year 13</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Filter by Stream</label>
              <Select value={filterStream} onValueChange={setFilterStream}>
                <SelectTrigger className="w-40" data-testid="filter-stream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Streams</SelectItem>
                  <SelectItem value="Science">Science</SelectItem>
                  <SelectItem value="Commerce">Commerce</SelectItem>
                  <SelectItem value="Arts">Arts</SelectItem>
                  <SelectItem value="Not Assigned">Not Assigned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
          </div>

          {/* Bulk Assignment Row */}
          {selectedStudents.length > 0 && (
            <div className="flex flex-wrap gap-4 items-end p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">
                  {selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} selected
                </span>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Assign Stream</label>
                <Select value={bulkStream} onValueChange={(v) => { setBulkStream(v); setBulkOptional(''); }}>
                  <SelectTrigger className="w-36" data-testid="bulk-stream">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Science">Science</SelectItem>
                    <SelectItem value="Commerce">Commerce</SelectItem>
                    <SelectItem value="Arts">Arts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {bulkStream && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Optional Subject</label>
                  <Select value={bulkOptional} onValueChange={setBulkOptional}>
                    <SelectTrigger className="w-36" data-testid="bulk-optional">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {STREAM_SUBJECTS[bulkStream]?.optional.map(subj => (
                        <SelectItem key={subj} value={subj}>{subj}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Button 
                onClick={handleBulkAssign}
                disabled={!bulkStream || !bulkOptional}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="bulk-assign-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Apply to Selected
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={() => setSelectedStudents([])}
              >
                Clear Selection
              </Button>
            </div>
          )}

          {/* Students Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                      onCheckedChange={selectAll}
                      data-testid="select-all"
                    />
                  </TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Current Stream</TableHead>
                  <TableHead>Optional Subject</TableHead>
                  <TableHead>Subjects</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading students...
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500">No students found for Year {year}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map(student => {
                    const streamDef = STREAM_SUBJECTS[student.stream];
                    return (
                      <TableRow key={student.id} className={selectedStudents.includes(student.id) ? 'bg-blue-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedStudents.includes(student.id)}
                            onCheckedChange={() => toggleStudent(student.id)}
                            data-testid={`select-${student.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{student.name}</p>
                            <p className="text-xs text-gray-500">{student.id}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {student.stream === 'Not Assigned' ? (
                            <Badge variant="outline" className="text-gray-500">
                              Not Assigned
                            </Badge>
                          ) : (
                            <Badge className={streamDef?.color || ''}>
                              <StreamIconComponent stream={student.stream} />
                              <span className="ml-1">{student.stream}</span>
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {student.optional ? (
                            <span className="text-sm">{student.optional}</span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {student.subjects.slice(0, 4).map(subj => (
                              <Badge key={subj} variant="outline" className="text-xs">
                                {subj}
                              </Badge>
                            ))}
                            {student.subjects.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{student.subjects.length - 4}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <StudentQuickAssign 
                            student={student}
                            year={year}
                            onAssign={handleStudentStreamChange}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Summary */}
          <div className="flex justify-between items-center text-sm text-gray-500 pt-2">
            <span>
              Showing {filteredStudents.length} of {students.length} students
            </span>
            <div className="flex gap-4">
              <span className="flex items-center gap-1">
                <Beaker className="w-4 h-4 text-purple-600" />
                Science: {students.filter(s => s.stream === 'Science').length}
              </span>
              <span className="flex items-center gap-1">
                <Calculator className="w-4 h-4 text-green-600" />
                Commerce: {students.filter(s => s.stream === 'Commerce').length}
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="w-4 h-4 text-amber-600" />
                Arts: {students.filter(s => s.stream === 'Arts').length}
              </span>
              <span className="flex items-center gap-1">
                <AlertCircle className="w-4 h-4 text-gray-400" />
                Unassigned: {students.filter(s => s.stream === 'Not Assigned').length}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Quick assign component for individual students
function StudentQuickAssign({ student, year, onAssign }) {
  const [stream, setStream] = useState(student.stream !== 'Not Assigned' ? student.stream : '');
  const [optional, setOptional] = useState(student.optional || '');
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    onAssign(student.id, stream, optional);
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => setIsEditing(true)}
        data-testid={`edit-${student.id}`}
      >
        Edit
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={stream} onValueChange={(v) => { setStream(v); setOptional(''); }}>
        <SelectTrigger className="w-24 h-8 text-xs">
          <SelectValue placeholder="Stream" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Science">Science</SelectItem>
          <SelectItem value="Commerce">Commerce</SelectItem>
          <SelectItem value="Arts">Arts</SelectItem>
        </SelectContent>
      </Select>
      
      {stream && (
        <Select value={optional} onValueChange={setOptional}>
          <SelectTrigger className="w-24 h-8 text-xs">
            <SelectValue placeholder="Option" />
          </SelectTrigger>
          <SelectContent>
            {STREAM_SUBJECTS[stream]?.optional.map(subj => (
              <SelectItem key={subj} value={subj}>{subj}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      <Button 
        size="sm" 
        className="h-8 px-2"
        onClick={handleSave}
        disabled={!stream || !optional}
      >
        <Check className="w-4 h-4" />
      </Button>
      <Button 
        size="sm" 
        variant="ghost"
        className="h-8 px-2"
        onClick={() => setIsEditing(false)}
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}
